import { useEffect, useState } from "react";
import { fetchProfile } from "../../Services/profile/profileservice";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { Copy } from "lucide-react";

export default function ApiIntegrationGuide() {
  const [env, setEnv] = useState("uat");
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const baseUrl =
    env === "uat"
      ? "https://javabackend.idspay.in/api/v1/uat"
      : "https://javabackend.idspay.in/api/v1/prod";

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    showSuccessToast(`Copied: ${text}`);
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r rounded-2xl from-[#0f2a6b] via-[#123c9c] to-[#1a4ed8] text-white p-7 shadow-xl">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold tracking-wide">
            IDSPay API Integration Guide
          </h1>
          <p className="text-sm opacity-90 mt-1">
            Secure • Fast • Fintech Ready
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* API Readiness Checklist */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-[#0f2a6b] mb-4">
            API Readiness Checklist
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <StatusCard
              color={profileData?.api_keys?.status_id === 5 ? "green" : "red"}
              text={
                profileData?.api_keys?.status_id === 5
                  ? "Api Account is active"
                  : "Api Account not active"
              }
            />
            <StatusCard
              color={profileData?.ips[0]?.status_id === 5 ? "green" : "red"}
              text={
                profileData?.api_keys?.status_id === 5
                  ? "IP whitelisted"
                  : "IP not whitelisted"
              }
            />
            <StatusCard
              color={profileData?.digital_kyc_status_id === 2 ? "green" : "red"}
              text={
                profileData?.digital_kyc_status_id === 2
                  ? "Digital KYC Completed"
                  : "Complete your digital KYC"
              }
            />
          </div>
        </div>

        {/* Integration Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-[#0f2a6b] mb-5">
            Integration Steps
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <StepCard
              title="1. Activate API Account"
              desc="Your IDSPay API account must be active before using any endpoint."
              color="blue"
            />

            <StepCard
              link="/WhiteListIP"
              title="2. Whitelist Your IP Address"
              desc="Add your server/local IP from IP Whitelist menu. Token will be generated after approval."
              color="red"
            />

            <StepCard
              link="/ServicePage"
              title="3. Check API service status"
              desc="API Service either in UAT or Production environment."
              color="green"
            />

            <StepCard
              title="4. Use Correct Base URL"
              desc="Use environment-based base URL with proper endpoint and POST method."
              color="indigo"
            />
          </div>
        </div>

        {/* Environment Switch */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-[#0f2a6b] mb-4">
            Base URL as per environment
          </h2>

          <div className="flex gap-3 mb-4">
            <button
              onClick={() => setEnv("uat")}
              className={`px-5 py-2 rounded-lg text-sm font-semibold shadow ${
                env === "uat"
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              UAT
            </button>

            <button
              onClick={() => setEnv("prod")}
              className={`px-5 py-2 rounded-lg text-sm font-semibold ${
                env === "prod"
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Production
            </button>
          </div>

          <div className="bg-slate-100 p-4 rounded-lg text-sm flex items-center gap-2 text-gray-700">
            Base URL:
            <span className="font-mono text-[#123c9c] ml-2">{baseUrl}</span>
            <button type="button" onClick={() => copyText(baseUrl)}>
              <Copy />
            </button>
          </div>
        </div>

        {/* API Key Structure */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-[#0f2a6b] mb-4">
            API Key Structure
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <KeyCard
              label="API ID"
              value={profileData?.user_id}
              onCopy={() => copyText(profileData?.user_id)}
            />
            <KeyCard
              label="API Key"
              value={profileData?.api_keys?.key}
              onCopy={() => copyText(profileData?.api_keys?.key)}
            />
            <KeyCard
              label="Token"
              value={profileData?.ips[0]?.token_id}
              onCopy={() => copyText(profileData?.ips[0]?.token_id)}
            />
          </div>

          <p className="text-xs text-gray-500 mt-3">
            UserID, API Key & Token are same for UAT and Production.
          </p>
        </div>

        {/* Request Format */}
        {/* <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-[#0f2a6b] mb-3">
            API Request Format
          </h2>

          <div className="bg-[#0f172a] text-green-400 text-sm rounded-xl p-4 overflow-auto">
            {`POST /endpoint

Headers:
UserID: <Your UserID>
APIKey: <Your API Key>
Token: <Your Token>
Content-Type: application/json

Method: POST`}
          </div>
        </div> */}

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 pt-4">
          © 2026 IDSPay • Secure API Documentation
        </div>
      </div>
    </div>
  );
}

/* 🔹 Reusable Components */

function StatusCard({ color, text }) {
  const styles = {
    green: "bg-green-50 border-green-200 text-green-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };

  return (
    <div
      className={`flex items-center gap-3 p-4 border rounded-xl ${styles[color]}`}
    >
      <div
        className={`w-3 h-3 rounded-full ${
          color === "green" ? "bg-green-500" : "bg-red-500"
        }`}
      ></div>
      <span className="text-sm font-medium">{text}</span>
    </div>
  );
}

function StepCard({ title, desc, color, link }) {
  const border = {
    blue: "border-[#1a4ed8]",
    red: "border-red-500 bg-red-50",
    green: "border-green-500 bg-green-50",
    indigo: "border-[#123c9c]",
  };

  return (
    <div className={`p-5 border-l-4 rounded-xl bg-slate-50 ${border[color]}`}>
      <h3 className="font-semibold text-gray-800">{title}</h3>
      <p className="text-sm text-gray-600 mt-1">{desc}</p>

      {link && (
        <div>
          <a
            href={link}
            className={`flex w-fit p-3 rounded-xl underline text-blue-700  items-center mt-2 font-semibold text-sm`}
          >
            <span className={``}>
              {link === "/ServicePage"
                ? "Click here to visit developers api"
                : "Click here to whitelist ip"}
            </span>
          </a>
        </div>
      )}
    </div>
  );
}

function KeyCard({ label, value, onCopy }) {
  return (
    <div className="bg-slate-50 border rounded-xl p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <div className="flex justify-between mt-1">
        <span className="font-mono text-sm">{value}</span>
        <button
          onClick={onCopy}
          className="text-blue-600 text-xs font-semibold"
        >
          Copy
        </button>
      </div>
    </div>
  );
}
